import { memo, useState } from "react";
import type { Column, WithKey } from "@clintonelec/react-storybook";
import { Button, Input, Table } from "@clintonelec/react-storybook";
import type { ApiHostname, HostnameBase, UserResource } from "Interfaces";
import Icon from "Components/Icon";
import "Components/HostnameTable/HostnameTable.less";
import { formatDate } from "@clintonelec/typescriptutils";
import { useAppDispatch } from "Data/Redux/Store";
import { NotificationType, notify, notifyError } from "src/Notifications";
import type { SerializedError } from "@reduxjs/toolkit";
import { produce } from "immer";
import { createUserHostnameAction } from "Data/Redux/Slices/User";
import { createHostnameAction, updateHostnameAction } from "Data/Redux/Slices/Hostnames";

const hostnameTableColumns: Column[] = [
	{
		key: "hostname",
		title: "Hostname",
		dataIndex: "hostname"
	},
	{
		key: "macAddress",
		title: "MAC Address",
		dataIndex: "macAddress"
	},
	{
		key: "ipv4",
		title: "IPv4 Address",
		dataIndex: "ipv4"
	},
	{
		key: "ipv6",
		title: "IPv6 Address",
		dataIndex: "ipv6"
	},
	{
		key: "lastUpdate",
		title: "Last Update",
		dataIndex: "lastUpdate"
	},
	{
		key: "online",
		title: "Online",
		dataIndex: "online"
	},
	{
		key: "action",
		title: "Action",
		dataIndex: "action"
	}
];

type HostnameTableProps = {
	hostnames: ApiHostname[];
	selectedUser?: UserResource;
};

const HostnameTable = ({ hostnames, selectedUser }: HostnameTableProps) => {
	const dispatch = useAppDispatch();

	const createHostname = (hostname: HostnameBase) => {
		if (selectedUser) {
			return dispatch(createUserHostnameAction({ ...hostname, userId: selectedUser.id }));
		} else {
			return dispatch(createHostnameAction(hostname));
		}
	};

	const updateHostname = (hostname: ApiHostname) => dispatch(updateHostnameAction(hostname));
	const [ editedHosts, setEditedHosts ] = useState<Record<string, ApiHostname>>({});
	const [ isAddingHostname, setIsAddingHostname ] = useState(false);
	const [ submitted, setSubmitted ] = useState(false);
	const [ hostnameErrors, setHostnameErrors ] = useState<Record<string, string>>({});

	const handleSaveEditClick = (hostname: ApiHostname) => () => {
		if (isAddingHostname) {
			return;
		} else if (editedHosts[ hostname.id ]) {
			// Hostname exists, so we want to edit it
			updateHostname(editedHosts[ hostname.id ])
				.unwrap()
				.then(() => {
					setEditedHosts(produce(editedHosts, draft => {
						delete draft[ hostname.id ];
					}));
				})
				.catch((error: SerializedError) => {
					notifyError(error);
				});
		} else {
			// Hostname didn't exist, so we add it
			setEditedHosts(produce(editedHosts, draft => {
				draft[ hostname.id ] = hostname;
			}));
		}
	};

	const handleCancelClick = (hostnameId: string) => () => {
		setEditedHosts(produce(editedHosts, draft => {
			delete draft[ hostnameId ];
		}));
	};

	const handleHostnameUpdate = (hostnameId: string) => (value: string) => {
		setEditedHosts(produce(editedHosts, (draft: Record<string, ApiHostname>) => {
			draft[ hostnameId ].hostname = value;
		}));
	};

	const handleIpv4Update = (hostnameId: string) => (value: string) => {
		setEditedHosts(produce(editedHosts, (draft: Record<string, ApiHostname>) => {
			draft[ hostnameId ].ipv4 = value;
		}));
	};

	const handleIpv6Update = (hostnameId: string) => (value: string) => {
		setEditedHosts(produce(editedHosts, (draft: Record<string, ApiHostname>) => {
			draft[ hostnameId ].ipv6 = value;
		}));
	};

	let tableData: WithKey[] = hostnames.map(current => {
		const { id, hostname, ipv4, ipv6, offline, lastUpdated } = current;
		const statusIcon: string = offline ? "circle-xmark" : "circle-check";
		const editedHost = editedHosts[ id ];
		const icon = editedHost ? "save" : "pencil";
		const ipv4Field = !editedHost
			? ipv4 ?? "—"
			: <Input value={ editedHost.ipv4 } onUpdate={ handleIpv4Update(id) } />;

		const ipv6Field = !editedHost
			? ipv6 ?? "—"
			: <Input value={ editedHost.ipv6 } onUpdate={ handleIpv6Update(id) } />;

		const compositeClass = [ "edit-hostname", isAddingHostname ? "disabled" : undefined ].join(" ");
		const actions = (
			<div className="hostname-actions">
				<Icon name={ icon } className={ compositeClass } onClick={ handleSaveEditClick(current) } />
				{ editedHost && <Icon name="xmark" className="edit-hostname" onClick={ handleCancelClick(id) } /> }
			</div>
		);

		return {
			key: id,
			hostname,
			ipv4: ipv4Field,
			ipv6: ipv6Field,
			lastUpdate: lastUpdated ? formatDate(new Date(lastUpdated), "MMM DD, YYYY - hh:mm:ss") : "—",
			online: <Icon name={ statusIcon } className={ offline ? "icon-error" : "icon-success" } />,
			action: actions
		};
	});

	const handleHostnameDeleteClick = () => {
		setIsAddingHostname(false);
		setEditedHosts({});
	};

	const handleHostnameSaveClick = () => {
		setSubmitted(true);

		createHostname(editedHosts[ "new" ])
			.unwrap()
			.then(() => {
				notify(NotificationType.SUCCESS, "Hostname created successfully.");
				setIsAddingHostname(false);
				setSubmitted(false);
			})
			.catch((error: SerializedError) => {
				try {
					setHostnameErrors(JSON.parse(error.message ?? ""));
					notify(NotificationType.ERROR, "There was an error creating the hostname.");
				} catch (e) {
					console.error("Error parsing hostname creation errors:", e);
				}
			});
	};

	if (isAddingHostname) {
		const hostnameActions = (
			<div className="hostname-actions">
				<Icon name="save" className="edit-hostname" onClick={ handleHostnameSaveClick } />
				<Icon name="trash" className="edit-hostname" onClick={ handleHostnameDeleteClick } />
			</div>
		);

		tableData = [
			{
				key: "new_hostname",
				hostname: (
					<Input
						placeholder="Hostname"
						onUpdate={ handleHostnameUpdate("new") }
						validator={ () => !hostnameErrors[ "hostname" ] }
						validityMessage={ hostnameErrors[ "hostname" ] }
						noValidate={ !submitted }
						required
					/>
				),
				ipv4: (
					<Input
						placeholder="IPv4 Address"
						onUpdate={ handleIpv4Update("new") }
						validator={ () => !hostnameErrors[ "ipv4" ] }
						validityMessage={ hostnameErrors[ "ipv4" ] }
						noValidate={ !submitted }
					/>
				),
				ipv6: (
					<Input
						placeholder="IPv6 Address"
						onUpdate={ handleIpv6Update("new") }
						validator={ () => !hostnameErrors[ "ipv6" ] }
						validityMessage={ hostnameErrors[ "ipv6" ] }
						noValidate={ !submitted }
					/>
				),
				action: hostnameActions
			},
			...tableData
		];
	}

	const handleAddHostnameClick = () => {
		setIsAddingHostname(true);

		setEditedHosts(produce(editedHosts, draft => {
			draft[ "new" ] = {
				id: "new",
				hostname: "",
				ipv4: "",
				ipv6: "",
				lastUpdated: new Date().toISOString(),
				offline: true
			};
		}));
	};

	return (
		<div className="hostname-table">
			<div className="action-bar">
				<Button
					icon={ { name: "plus" } }
					onClick={ handleAddHostnameClick }
					disabled={ isAddingHostname || Object.keys(editedHosts).length > 0 }
				>
					Add Hostname
				</Button>
			</div>
			<Table
				columns={ hostnameTableColumns }
				data={ tableData }
			/>
		</div>
	);
};

export default memo(HostnameTable);
